import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Table, Spinner, Form, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { emailBroadcastService } from '@/_services/api/emailIntegration/emailBroadcast';
import EmailBroadcastStatusBadge from './EmailBroadcastStatusBadge';

const recipientStatusBadge = (status) => {
    const map = {
        pending: { bg: 'secondary', label: 'Pending' },
        sending: { bg: 'warning', label: 'Sending' },
        sent: { bg: 'success', label: 'Sent' },
        failed: { bg: 'danger', label: 'Failed' },
        rate_limit: { bg: 'danger', label: 'Rate Limit' },
        cancelled: { bg: 'dark', label: 'Cancelled' },
    };
    const s = map[status] || map.pending;
    return <span className={`badge badge-${s.bg}`}>{s.label}</span>;
};

const EmailBroadcastDetailModal = ({ show, onHide, broadcastId }) => {
    const [broadcast, setBroadcast] = useState(null);
    const [recipients, setRecipients] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState('all');

    const fetchDetail = async () => {
        if (!broadcastId) return;
        setLoading(true);
        try {
            const [bRes, rRes] = await Promise.all([
                emailBroadcastService.getBroadcastById(broadcastId),
                emailBroadcastService.getBroadcastRecipients(broadcastId, { limit: 1000 }),
            ]);
            setBroadcast(bRes);
            setRecipients(rRes?.results || rRes || []);
        } catch (err) {
            if (err?.name === 'AbortError') return;
            toast.error(err?.message || 'Failed to load broadcast details');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!show || !broadcastId) return undefined;

        let isActive = true;
        let intervalId = null;

        const loadDetail = async () => {
            if (!isActive) return null;
            try {
                const [bRes, rRes] = await Promise.all([
                    emailBroadcastService.getBroadcastById(broadcastId),
                    emailBroadcastService.getBroadcastRecipients(broadcastId, { limit: 200 }),
                ]);
                if (!isActive) return bRes;
                setBroadcast(bRes);
                setRecipients(rRes?.results || rRes || []);
                return bRes;
            } catch (err) {
                if (err?.name === 'AbortError' || !isActive) return null;
                toast.error(err?.message || 'Failed to load broadcast details');
                return null;
            } finally {
                if (isActive) {
                    setLoading(false);
                }
            }
        };

        const start = async () => {
            setLoading(true);
            const first = await loadDetail();
            if (first?.status === 'sending' && isActive) {
                intervalId = setInterval(async () => {
                    const latest = await loadDetail();
                    if (latest && latest.status !== 'sending' && intervalId) {
                        clearInterval(intervalId);
                        intervalId = null;
                    }
                }, 3000);
            }
        };

        start();

        return () => {
            isActive = false;
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [show, broadcastId]);

    const filtered = filter === 'all' ? recipients : recipients.filter((r) => r.status === filter);
    const content = broadcast?.emailBroadcastDraftSnapshot || broadcast || {};

    return (
        <Modal show={show} onHide={onHide} size="xl" centered>
            <Modal.Header closeButton>
                <Modal.Title>{content.subject || 'Broadcast Details'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" />
                    </div>
                ) : (
                    <>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                                <strong>Status:</strong> <EmailBroadcastStatusBadge status={broadcast?.status} />
                            </div>
                            <div>
                                <strong>Sent:</strong> {broadcast?.sentCount || 0} / {broadcast?.totalRecipients || 0}
                            </div>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <Form.Control
                                as="select"
                                size="sm"
                                style={{ width: 180 }}
                                value={filter || 'all'} // ← Add fallback
                                onChange={(e) => setFilter(e.target.value)}
                            >
                                <option value="all">All statuses</option>
                                <option value="sent">Sent</option>
                                <option value="failed">Failed</option>
                                <option value="quota_hit">Rate Limit</option>
                                <option value="cancelled">Cancelled</option>
                                <option value="pending">Pending</option>
                            </Form.Control>
                            <Button size="sm" variant="outline-primary" onClick={fetchDetail}>
                                Refresh
                            </Button>
                        </div>
                        <Table striped bordered hover size="sm">
                            <thead>
                                <tr>
                                    <th style={{ width: 40 }}>#</th>
                                    <th>Email</th>
                                    <th>Name</th>
                                    <th>Status</th>
                                    <th>Sent At</th>
                                    <th>Opened At</th>
                                    <th>Error</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((r, idx) => (
                                    <tr key={r._id || idx}>
                                        <td>{idx + 1}</td>
                                        <td>{r.email}</td>
                                        <td>{r.name || '-'}</td>
                                        <td>{recipientStatusBadge(r.status)}</td>
                                        <td>{r.sentAt ? new Date(r.sentAt).toLocaleString() : '-'}</td>
                                        <td>{r.openedAt ? new Date(r.openedAt).toLocaleString() : '-'}</td>
                                        <td style={{ maxWidth: '200px', wordWrap: 'break-word' }}>
                                            {r?.status !== 'sent' && <span>{(r?.errorObj || {})?.message}</span>}
                                        </td>
                                    </tr>
                                ))}
                                {filtered.length === 0 && (
                                    <tr>
                                        <td colSpan="6" className="text-center text-muted">
                                            No recipients match this filter.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

EmailBroadcastDetailModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    broadcastId: PropTypes.string,
};

export default EmailBroadcastDetailModal;
